/**
 * Слои визуального языка Astra на полотне `MapCanvas` (решение 9 плана
 * `astra-on-canon`: один движок карты).
 *
 * Код перенесён из прототипа (`prototype/app/src/map/mapStyle.ts`) как есть:
 * заливка по выбранному слою, 45° штриховка «ожидается», перекрёстная
 * «демо», медная обводка кандидата, янтарь наблюдения, пунктир «без отчёта»,
 * наведение и выбор. Движок прототипа (`MapWidget` на maplibre 5) не
 * переносится — эти слои кладёт в стиль канонический `MapCanvas`.
 *
 * Отличия от прототипа только в обвязке: фон-слой `canvas` не нужен (фон
 * полотна канона — CSS контейнера), а идентификатор фичи — свойство `id`,
 * которое полотно продвигает в feature-state (`promoteId`).
 */

import { PALETTE } from '../../astra/styles/palette';
import { HATCH_DEMO, HATCH_PENDING } from '../../astra/map/hatch';
import {
  clusterColorExpression,
  pointColorExpression,
  SEVERITY_RING,
} from '../../astra/map/layers';

export const SOURCE_ID = 'contours';
/**
 * Точки статуса (agroai-6al.1): отдельный точечный источник с кластерами.
 * Имя прежнее — исторически здесь жили только точки «под наблюдением».
 */
export const WATCH_SOURCE_ID = 'quarantine-watch';
/** До какого зума точки собираются в кластеры. */
export const STATUS_CLUSTER_MAX_ZOOM = 11;
export const STATUS_CLUSTER_RADIUS = 36;

export const LAYERS = {
  fill: 'contours-fill',
  pending: 'contours-pending',
  demo: 'contours-demo',
  line: 'contours-line',
  candidate: 'contours-candidate',
  watch: 'contours-watch',
  silent: 'contours-silent',
  ring: 'contours-ring',
  hover: 'contours-hover',
  selectHalo: 'contours-select-halo',
  select: 'contours-select',
  watchDot: 'quarantine-watch-dot',
  statusCluster: 'status-cluster',
} as const;

export function contourLayers(fillColor: unknown): Record<string, unknown>[] {
  return [
    {
      id: LAYERS.fill,
      type: 'fill',
      source: SOURCE_ID,
      paint: {
        'fill-color': fillColor,
        'fill-opacity': 0.88,
      },
    },
    // «Ожидается / ещё не срок» — 45° штриховка. Только этот смысл.
    {
      id: LAYERS.pending,
      type: 'fill',
      source: SOURCE_ID,
      layout: { visibility: 'none' },
      paint: {
        'fill-pattern': HATCH_PENDING,
        'fill-opacity': [
          'case',
          ['any',
            ['==', ['to-string', ['feature-state', 'ws']], 'upcoming'],
            ['==', ['to-string', ['feature-state', 'ws']], 'in_window'],
          ],
          0.5,
          0,
        ],
      },
    },
    // Демо/синтетика — постоянная перекрёстная штриховка.
    {
      id: LAYERS.demo,
      type: 'fill',
      source: SOURCE_ID,
      filter: ['in', ['get', 'id'], ['literal', []]],
      paint: { 'fill-pattern': HATCH_DEMO, 'fill-opacity': 0.65 },
    },
    {
      id: LAYERS.line,
      type: 'line',
      source: SOURCE_ID,
      paint: {
        'line-color': PALETTE.lineStrong,
        'line-width': ['interpolate', ['linear'], ['zoom'], 9, 0.2, 13, 0.8],
        'line-opacity': 0.9,
      },
    },
    // Кандидатная геометрия — медная штриховая обводка плюс подпись в легенде.
    // На районном зуме тонкая, иначе тысячи кандидатов затапливают карту медью.
    {
      id: LAYERS.candidate,
      type: 'line',
      source: SOURCE_ID,
      filter: ['==', ['coalesce', ['get', 'geometry_status'], 'none'], 'candidate'],
      paint: {
        'line-color': PALETTE.copperDeep,
        'line-width': ['interpolate', ['linear'], ['zoom'], 9, 0.35, 11, 0.7, 13, 1.2],
        'line-opacity': ['interpolate', ['linear'], ['zoom'], 9, 0.45, 11, 0.8, 13, 1],
        'line-dasharray': [2, 2.5],
      },
    },
    // «Под наблюдением» (карантин) — янтарная обводка. Заливка watch почти
    // невидима на районном зуме, поэтому смысл несут обводка и точка-маркер.
    // Слой включается только на слое «Карантин». Медь отсюда ушла: она
    // означает кандидатность связи, а наблюдение — состояние (PALETTE-SPEC).
    {
      id: LAYERS.watch,
      type: 'line',
      source: SOURCE_ID,
      layout: { visibility: 'none' },
      paint: {
        // Красный карантин обводится красным, наблюдение — янтарём: на
        // районном зуме смысл несут обводка и точка, а не почти невидимая
        // мягкая заливка (agroai-6al.1).
        'line-color': [
          'case',
          ['==', ['to-string', ['feature-state', 'q']], 'red'],
          PALETTE.stAlert,
          PALETTE.stWarn,
        ],
        // ['zoom'] допустим только на входе ВЕРХНЕГО interpolate: вложить
        // масштабирование внутрь ['case'] нельзя — стиль отвергнет слой.
        'line-width': [
          'interpolate',
          ['linear'],
          ['zoom'],
          9,
          ['match', ['to-string', ['feature-state', 'q']], 'red', 1.6, 'watch', 1.2, 0],
          13,
          ['match', ['to-string', ['feature-state', 'q']], 'red', 2.8, 'watch', 2.2, 0],
        ],
        'line-opacity': 0.95,
      },
    },
    // «Без отчёта» — пунктир нейтральным тоном, без красного. Цвет здесь
    // тёмно-нейтральный, а не `st-missing`: пунктир идёт ПО заливке
    // `st-missing` и одноимённым тоном был бы невидим.
    {
      id: LAYERS.silent,
      type: 'line',
      source: SOURCE_ID,
      paint: {
        'line-color': PALETTE.muted,
        'line-dasharray': [1, 2],
        'line-width': [
          'case',
          ['==', ['to-string', ['feature-state', 'ws']], 'silent'],
          1.1,
          0,
        ],
      },
    },
    // Отметка страницы (`ringIds`): тёмная обводка поверх заливки — второй
    // признак контура (например, рецидив на тепловой карте). Смысл называет
    // легенда страницы; список id — фильтром, как у демо-штриховки.
    {
      id: LAYERS.ring,
      type: 'line',
      source: SOURCE_ID,
      filter: ['in', ['get', 'id'], ['literal', []]],
      paint: {
        'line-color': PALETTE.ink,
        'line-width': ['interpolate', ['linear'], ['zoom'], 9, 1.2, 13, 2.4],
        'line-opacity': 0.9,
      },
    },
    {
      id: LAYERS.hover,
      type: 'line',
      source: SOURCE_ID,
      paint: {
        'line-color': PALETTE.deep,
        'line-width': 1.2,
        'line-opacity': ['case', ['boolean', ['feature-state', 'hover'], false], 0.9, 0],
      },
    },
    {
      id: LAYERS.selectHalo,
      type: 'line',
      source: SOURCE_ID,
      filter: ['==', ['get', 'id'], '__none__'],
      paint: { 'line-color': PALETTE.card, 'line-width': 5 },
    },
    {
      id: LAYERS.select,
      type: 'line',
      source: SOURCE_ID,
      filter: ['==', ['get', 'id'], '__none__'],
      paint: { 'line-color': PALETTE.deep, 'line-width': 2 },
    },
  ];
}

/**
 * Точки статуса поверх контуров (agroai-6al.1). На районном зуме контур
 * занимает несколько пикселей, и одной заливки не хватает, чтобы красный,
 * жёлтый и зелёный были различимы. Поэтому у слоя с тяжестью (карантин,
 * статус работы, вода) каждая точка красится СВОЕЙ ступенью шкалы, а на
 * отдалении точки собираются в кластеры, окрашенные худшей тяжестью внутри:
 * красный в кластере из сотни жёлтых остаётся красным. Красная точка крупнее —
 * критичное не должно теряться среди предупреждений.
 *
 * Прежний `watchDotLayer` красил все точки янтарём — замечание 4 плана страниц.
 */
export function statusPointLayers(): Record<string, unknown>[] {
  return [
    {
      id: LAYERS.statusCluster,
      type: 'circle',
      source: WATCH_SOURCE_ID,
      filter: ['has', 'point_count'],
      layout: { visibility: 'none' },
      paint: {
        'circle-color': clusterColorExpression(),
        // Размер говорит о числе контуров, ступенями: подписи числом нет
        // (у полотна нет глифов), счёт живёт в подсказке и легенде.
        'circle-radius': ['step', ['get', 'point_count'], 7, 10, 9, 50, 12, 200, 15],
        'circle-opacity': 0.92,
        'circle-stroke-color': PALETTE.card,
        'circle-stroke-width': 2,
      },
    },
    {
      id: LAYERS.watchDot,
      type: 'circle',
      source: WATCH_SOURCE_ID,
      filter: ['!', ['has', 'point_count']],
      layout: { visibility: 'none' },
      paint: {
        'circle-color': pointColorExpression(),
        'circle-radius': [
          'interpolate',
          ['linear'],
          ['zoom'],
          8,
          ['match', ['get', 'severity'], 'critical', 4.5, 3.5],
          13,
          ['match', ['get', 'severity'], 'critical', 7.5, 6],
        ],
        // Тёмное кольцо своей ступени: янтарная и жёлтая точки держат к
        // светлым заливкам 2,4:1 и 1,8:1, белое кольцо их бы не отделило.
        'circle-stroke-color': pointColorExpression(SEVERITY_RING),
        'circle-stroke-width': 1.5,
      },
    },
  ];
}

/** Прежнее имя: одиночные точки без кластеров (совместимость импортов). */
export function watchDotLayer(): Record<string, unknown> {
  return statusPointLayers()[1]!;
}
